import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import chatSessionsAPI from '../api/chatSessions';
import chatMessagesAPI from '../api/chatMessages';
import ChatWebSocket from '../services/chatWebSocket';
import LoadingDots from './LoadingDots';
import transcriptionAPI from '../api/transcription';
import './Dashboard.css';

const Dashboard = () => {
  const { user, userId, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartMs, setRecordingStartMs] = useState(null);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [recordings, setRecordings] = useState([]); // { id, url, blob, durationMs }
  const [isTranscribing, setIsTranscribing] = useState(false);
  // Long-press/modal state
  const [isInstructionModalOpen, setIsInstructionModalOpen] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  // Loading states for different actions
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const timerRef = useRef(null);

  const handleSendMessage = async () => {
    // If we have recordings, transcribe them first
    if (recordings.length > 0) {
      await handleTranscribeAndSend();
      return;
    }

    if (!inputText.trim()) return;
    
    // If no session is selected, create a new one first
    if (!currentSessionId) {
      await handleNewChat();
      return;
    }
    
    if (!wsRef.current) return;

    const messageText = inputText.trim();
    setInputText('');

    try {
      // Create user message immediately
      const userMessage = {
        id: Date.now(), // Temporary ID
        role: 'user',
        content: messageText,
        created_at: new Date().toISOString(),
        chat_session_id: currentSessionId,
        streams: []
      };

      setMessages(prev => [...prev, userMessage]);

      // Send message via WebSocket
      wsRef.current.sendMessage(messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleTranscribeAndSend = async () => {
    if (recordings.length === 0) return;
    
    setIsTranscribing(true);
    
    try {
      // If no session is selected, create a new one first
      if (!currentSessionId) {
        await handleNewChat();
      }
      
      if (!wsRef.current) return;

      // Transcribe all recordings
      const transcriptions = [];
      for (const recording of recordings) {
        try {
          const transcription = await transcriptionAPI.transcribeAudio(recording.blob);
          if (transcription) {
            transcriptions.push(transcription);
          }
        } catch (error) {
          console.error('Failed to transcribe recording:', error);
        }
      }

      if (transcriptions.length === 0) {
        alert('Failed to transcribe any recordings. Please try again.');
        return;
      }

      // Combine all transcriptions
      const combinedText = transcriptions.join(' ');
      setInputText(combinedText);

      // Create user message immediately
      const userMessage = {
        id: Date.now(), // Temporary ID
        role: 'user',
        content: combinedText,
        created_at: new Date().toISOString(),
        chat_session_id: currentSessionId,
        streams: []
      };

      setMessages(prev => [...prev, userMessage]);

      // Send message via WebSocket
      wsRef.current.sendMessage(combinedText);

      // Clear recordings and input text after successful send
      setRecordings([]);
      setInputText('');
    } catch (error) {
      console.error('Failed to transcribe and send:', error);
      alert('Failed to transcribe recordings. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  // Recording helpers
  const formatElapsed = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const startTimer = (startMs) => {
    setRecordingStartMs(startMs);
    setRecordingElapsedMs(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRecordingElapsedMs(Date.now() - startMs);
    }, 200);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Long-press detection for mic button
  const LONG_PRESS_MS = 600; // threshold for considering a long press

  const handleMicPointerDown = (e) => {
    // Prevent default to avoid context menu/long-press actions on mobile
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    longPressTriggeredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setIsInstructionModalOpen(true);
    }, LONG_PRESS_MS);
  };

  const handleMicPointerUp = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    // If long-press did not trigger, treat as a tap/click
    if (!longPressTriggeredRef.current) {
      startRecording();
    }
  };

  const handleMicPointerLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const startRecording = async () => {
    if (isRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      // Try to use a more mobile-friendly format
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
        mimeType = 'audio/mpeg';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const durationMs = Date.now() - (recordingStartMs || Date.now());
        setRecordings((prev) => [
          { id: Date.now(), url, blob, durationMs, mimeType },
          ...prev,
        ]);
        stopTimer();
        setRecordingStartMs(null);
        setRecordingElapsedMs(0);
      };
      mediaRecorder.start();
      setIsRecording(true);
      startTimer(Date.now());
    } catch (err) {
      console.error('Failed to start recording:', err);
      setIsRecording(false);
      stopTimer();
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {
      console.error('Failed to stop MediaRecorder:', e);
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    setIsRecording(false);
  };

  const deleteRecording = (id) => {
    setRecordings((prev) => {
      const rec = prev.find((r) => r.id === id);
      if (rec && rec.url) URL.revokeObjectURL(rec.url);
      return prev.filter((r) => r.id !== id);
    });
  };

  const handleNewChat = async () => {
    if (!userId) return;
    
    setIsCreatingSession(true);
    try {
      const newSession = await chatSessionsAPI.createSession(userId, 'New Chat');
      const formattedSession = chatSessionsAPI.formatSessionForDisplay(newSession);
      setChatSessions(prev => [formattedSession, ...prev]);
      setCurrentSessionId(newSession.id);
      setMessages([]);
      setIsSidebarOpen(false); // Close sidebar on mobile after selecting
    } catch (error) {
      console.error('Failed to create new chat session:', error);
    } finally {
      setIsCreatingSession(false);
    }
  };

  const handleSelectSession = async (sessionId) => {
    setCurrentSessionId(sessionId);
    setIsSidebarOpen(false); // Close sidebar on mobile after selecting
    
    setIsLoadingMessages(true);
    try {
      const sessionMessages = await chatMessagesAPI.getSessionMessages(sessionId, 100, 0);
      if (Array.isArray(sessionMessages)) {
        setMessages(sessionMessages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to load session messages:', error);
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleDeleteSession = async (sessionId, event) => {
    event.stopPropagation(); // Prevent session selection when clicking delete
    
    if (!window.confirm('Are you sure you want to delete this chat session? This action cannot be undone.')) {
      return;
    }
    
    setIsDeletingSession(sessionId);
    try {
      await chatSessionsAPI.deleteSession(sessionId);
      
      // Remove from local state
      setChatSessions(prev => prev.filter(session => session.id !== sessionId));
      
      // If this was the current session, clear it
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to delete chat session:', error);
      alert('Failed to delete chat session. Please try again.');
    } finally {
      setIsDeletingSession(null);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // WebSocket event handlers
  const handleMessageStart = (data) => {
    console.log('Message start:', data);
    setIsLoading(true);
    
    // Create a new assistant message with loading state
    const assistantMessage = {
      id: data.messageId,
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
      chat_session_id: currentSessionId,
      streams: [],
      isLoading: true
    };
    
    setStreamingMessage(assistantMessage);
    setMessages(prev => [...prev, assistantMessage]);
  };

  const handleMessageEnd = (data) => {
    console.log('Message end:', data);
    setIsLoading(false);
    setStreamingMessage(null);
    
    // Update the message to remove loading state
    setMessages(prev => prev.map(msg => 
      msg.id === data.messageId 
        ? { ...msg, isLoading: false }
        : msg
    ));
  };

  const handleStreamCreate = (data) => {
    console.log('Stream create:', data);
    
    const newStream = {
      stream_id: data.streamId,
      content: '',
      created_at: data.createdAt
    };
    
    // Update the message in the messages array
    setMessages(prev => prev.map(msg => 
      msg.id === data.messageId 
        ? { ...msg, streams: [...(msg.streams || []), newStream] }
        : msg
    ));
    
    // Also update the streaming message if it matches
    if (streamingMessage && streamingMessage.id === data.messageId) {
      setStreamingMessage(prev => ({
        ...prev,
        streams: [...(prev.streams || []), newStream]
      }));
    }
  };

  const handleStreamDelta = (data) => {
    console.log('🟢 STREAM_DELTA handler called:', data);
    
    // Update the message in the messages array
    setMessages(prev => {
      const updatedMessages = prev.map(msg => {
        if (msg.id === data.messageId) {
          const updatedStreams = (msg.streams || []).map(stream => {
            if (stream.stream_id === data.streamId) {
              const newContent = stream.content + (data.delta || '');
              console.log(`🟢 Updating stream ${data.streamId}: "${stream.content}" + "${data.delta}" = "${newContent}"`);
              return { ...stream, content: newContent };
            }
            return stream;
          });
          return { ...msg, streams: updatedStreams };
        }
        return msg;
      });
      return updatedMessages;
    });
    
    // Also update the streaming message if it matches
    if (streamingMessage && streamingMessage.id === data.messageId) {
      setStreamingMessage(prev => ({
        ...prev,
        streams: (prev.streams || []).map(stream => 
          stream.stream_id === data.streamId
            ? { ...stream, content: stream.content + (data.delta || '') }
            : stream
        )
      }));
    }
  };

  // Initialize WebSocket connection
  const initializeWebSocket = async (sessionId) => {
    if (wsRef.current) {
      wsRef.current.destroy();
    }

    wsRef.current = new ChatWebSocket();
    
    // Set up event handlers
    console.log('🔧 Setting up WebSocket event handlers');
    wsRef.current.on('message_start', handleMessageStart);
    wsRef.current.on('message_end', handleMessageEnd);
    wsRef.current.on('stream_create', handleStreamCreate);
    wsRef.current.on('stream_delta', handleStreamDelta);
    wsRef.current.on('error', (error) => {
      console.error('WebSocket error:', error);
    });
    console.log('🔧 WebSocket event handlers registered');

    try {
      await wsRef.current.connect(sessionId);
      console.log('WebSocket connected for session:', sessionId);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [inputText]);

  // Log userId for debugging
  useEffect(() => {
    console.log('Current user ID:', userId);
  }, [userId]);

  // Log messages changes for debugging
  useEffect(() => {
    console.log('📝 Messages state changed:', messages);
  }, [messages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  // Load chat sessions when userId is available
  useEffect(() => {
    const loadChatSessions = async () => {
      if (!userId) return;
      
      setIsLoadingSessions(true);
      try {
        const sessions = await chatSessionsAPI.getUserSessions(userId, 50, 0);
        const formattedSessions = sessions.map(session => 
          chatSessionsAPI.formatSessionForDisplay(session)
        );
        setChatSessions(formattedSessions);
      } catch (error) {
        console.error('Failed to load chat sessions:', error);
      } finally {
        setIsLoadingSessions(false);
      }
    };

    loadChatSessions();
  }, [userId]);

  // Initialize WebSocket when currentSessionId changes
  useEffect(() => {
    if (currentSessionId) {
      initializeWebSocket(currentSessionId);
    }

    // Cleanup WebSocket on unmount or session change
    return () => {
      if (wsRef.current) {
        wsRef.current.destroy();
        wsRef.current = null;
      }
    };
  }, [currentSessionId]);

  // Cleanup WebSocket on component unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.destroy();
      }
      // Cleanup recording resources
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordings((prev) => {
        prev.forEach((r) => r.url && URL.revokeObjectURL(r.url));
        return prev;
      });
    };
  }, []);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-nav">
          <div className="nav-left">
            <button className="hamburger-button" onClick={toggleSidebar}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
              </svg>
            </button>
            <h1>Axentric Knowledge</h1>
          </div>
          <div className="user-info">
            <span>Welcome, {user?.username || user?.email}</span>
            <button onClick={logout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>
      
      <div className="dashboard-content">
        {/* Sidebar */}
        <aside className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
          <div className="sidebar-header">
            <button 
              onClick={handleNewChat} 
              className="new-chat-button"
              disabled={isCreatingSession}
            >
              {isCreatingSession ? (
                <LoadingDots />
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                  Novi Razgovor
                </>
              )}
            </button>
          </div>
          <div className="chat-sessions">
            {isLoadingSessions ? (
              <div className="loading-sessions">
                <LoadingDots />
                <p>Loading sessions...</p>
              </div>
            ) : chatSessions.length === 0 ? (
              <div className="empty-sessions">
                <p>No chat sessions yet</p>
                <p>Start a new conversation!</p>
              </div>
            ) : (
              chatSessions.map((session) => (
                <div
                  key={session.id}
                  className={`session-card ${currentSessionId === session.id ? 'active' : ''}`}
                  onClick={() => handleSelectSession(session.id)}
                >
                  <div className="session-content">
                    <div className="session-title">{session.title}</div>
                    <div className="session-preview">{session.lastMessage}</div>
                    <div className="session-time">
                      {new Date(session.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="delete-session-button"
                    onClick={(e) => handleDeleteSession(session.id, e)}
                    title="Delete chat session"
                    disabled={isDeletingSession === session.id}
                  >
                    {isDeletingSession === session.id ? (
                      <LoadingDots />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                      </svg>
                    )}
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Overlay for mobile */}
        {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar} />}

        {/* Main chat area */}
        <main className="chat-main">
          <div className="chat-container">
            <div className="messages-container">
            {isLoadingMessages ? (
              <div className="loading-messages">
                <LoadingDots />
                <p>Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="empty-state">
                {!currentSessionId ? (
                  <div className="no-session-state">
                    <div className="no-session-content">
                      <h3>Bok! 🙂</h3>
                      <p>Započni razgovor da podijeliš svoje znanje s AI asistentom, pomažući mu razumjeti posao, alate i tvoje odgovornosti.</p>
                      <button 
                        onClick={handleNewChat}
                        className="start-chat-button"
                        disabled={isCreatingSession}
                      >
                        {isCreatingSession ? (
                          <LoadingDots />
                        ) : (
                          <>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                            </svg>
                            Započni Novi Razgovor
                          </>
                        )}
                      </button>
                      <div className="voice-hint" style={{ marginTop: '12px' }}>
                        <div className="voice-hint-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                          </svg>
                        </div>
                        <div className="voice-hint-text">
                          Možeš razgovarati glasom. Jednim pritiskom mikrofon gumba započni snimanje. Snimi snimku i pošalji je.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-session-state">
                    <div className="no-session-content">
                      <h3>Za početak opiši mi svoj radni dan na poslu u par rečenica. 🙂</h3>
                      <p>Započni razgovor tipkanjem ili snimanjem glasa.</p>
                      <div className="voice-hint" style={{ marginTop: '12px' }}>
                        <div className="voice-hint-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                          </svg>
                        </div>
                        <div className="voice-hint-text">
                          Možeš razgovarati glasom. Jednim pritiskom mikrofon gumba započni snimanje. Snimi snimku i pošalji je.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              messages.map((message, index) => {
                const isLastMessage = index === messages.length - 1;
                return (
                  <div 
                    key={`${message.id}-${message.streams?.length || 0}-${message.streams?.map(s => s.content?.length || 0).join('-') || 0}`} 
                    className={`message-container ${message.role === 'user' ? 'user-message-container' : 'assistant-message-container'} ${isLastMessage ? 'last-message' : ''}`}
                    style={isLastMessage ? { 
                      minHeight: window.innerWidth <= 768 ? '15vh' : '25vh',
                      marginBottom: '30vh'
                    } : {}}
                  >
                  {message.role === 'user' ? (
                    <div className="user-message-bubble">
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="assistant-message-text">
                      {(() => {
                        const streamContent = chatMessagesAPI.getStreamsContent(message.streams);
                        const hasStreams = message.streams && message.streams.length > 0;
                        const hasContent = streamContent && streamContent.trim().length > 0;
                        console.log('🎨 Rendering message streams:', {
                          messageId: message.id,
                          streams: message.streams,
                          streamContent: streamContent,
                          hasStreams: hasStreams,
                          hasContent: hasContent,
                          isLoading: message.isLoading
                        });
                        
                        if (hasStreams && hasContent) {
                          return <ReactMarkdown>{streamContent}</ReactMarkdown>;
                        } else if (message.isLoading || !hasContent) {
                          return <LoadingDots />;
                        } else {
                          return <ReactMarkdown>{streamContent}</ReactMarkdown>;
                        }
                      })()}
                    </div>
                  )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
            </div>
          </div>
          
          {currentSessionId && (
            <div className="input-container">
              <div className={`input-wrapper ${(isRecording || recordings.length > 0) ? 'input-wrapper-no-text' : ''}`}>
                {!isRecording && recordings.length === 0 && (
                  <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Upiši svoju poruku..."
                    className="message-input"
                    rows={1}
                  />
                )}
                {recordings.length > 0 && (
                  <div className="recordings-list">
                    {recordings.map((rec) => (
                      <div key={rec.id} className="recording-pill">
                        <div className="recording-icon" />
                        <audio 
                          src={rec.url} 
                          controls 
                          preload="metadata"
                          playsInline
                          webkit-playsinline="true"
                          style={{ maxWidth: '120px', height: '32px' }}
                          onError={(e) => {
                            console.error('Audio playback error:', e);
                            console.log('Audio format:', rec.mimeType || 'unknown');
                          }}
                        />
                        <span className="recording-duration">{formatElapsed(rec.durationMs)}</span>
                        <button className="recording-delete-button" title="Delete recording" onClick={() => deleteRecording(rec.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="input-buttons">
                  {!isRecording ? (
                    <button
                      className="mic-button"
                      title="Start recording"
                      onPointerDown={handleMicPointerDown}
                      onPointerUp={handleMicPointerUp}
                      onPointerLeave={handleMicPointerLeave}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                    </button>
                  ) : (
                    <div className="recording-controls">
                      <div className="recording-timer">
                        <span className="recording-dot" /> {formatElapsed(recordingElapsedMs)}
                      </div>
                      <button className="stop-recording-button" title="Stop recording" onClick={stopRecording}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <button 
                    onClick={handleSendMessage}
                    className="send-button"
                    disabled={(!inputText.trim() && recordings.length === 0) || isTranscribing}
                    title={
                      isTranscribing 
                        ? "Transcribing..." 
                        : recordings.length > 0 
                          ? "Transcribe and send recordings" 
                          : "Send message"
                    }
                  >
                    {isTranscribing ? (
                      <LoadingDots />
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      {isInstructionModalOpen && (
        <div className="modal-overlay" onClick={() => setIsInstructionModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Kako koristiti snimanje glasa</div>
            <div className="modal-body">
              Dodirni mikrofon jednom za početak snimanja, zatim pritisni stop kada završiš. Kada si spreman, pošalji snimku.
            </div>
            <div className="modal-actions">
              <button className="modal-button" onClick={() => setIsInstructionModalOpen(false)}>U redu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
